<template>
  <v-dialog
    v-if="!!et"
    :value="!!et"
    max-width="600px"
    persistent
    @keydown.esc="done"
    ><v-card>
      <v-card-title>
        <span class="headline"
          >{{ $t("extTrainings.extTraining") }} {{ extTraining.id }}</span
        >
        <v-spacer />
        <v-btn icon @click="done"><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle v-if="employee" class="pb-0 mt-2">
        {{
          $t("extTrainings.addSingle.subtitlePart1", {
            name: $options.filters.name(employee),
          })
        }}
        {{ $t("extTrainings.addSingle.subtitlePart2") }}
        <router-link :to="{ name: 'external-trainings' }">
          {{ $t("extTrainings.addSingle.subtitleLink") }}
        </router-link>
      </v-card-subtitle>
      <v-card-text>
        <v-container grid-list-md>
          <v-form ref="extTrainingEditForm">
            <v-row dense>
              <v-col cols="6">
                <v-autocomplete
                  v-model="et.categoryId"
                  :items="sortedCategories"
                  :label="$t('extTrainings.column.category')"
                  hide-details="auto"
                  item-value="id"
                  item-text="category"
                  v-on:change="changeCategory"
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="6">
                <v-text-field
                  v-model="et.durationMinutes"
                  type="number"
                  :label="$t('extTrainings.durationMinutes')"
                  :rules="[rules.required]"
                  hide-details="auto"
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="et.name"
                  :label="$t('extTrainings.column.trainingName')"
                  :rules="[rules.required]"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="et.educator"
                  :label="$t('extTrainings.column.educator')"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="et.location"
                  :label="$t('extTrainings.column.location')"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </v-col>

              <v-col cols="6">
                <v-dialog
                  v-model="datePickerModal"
                  persistent
                  width="290px"
                  @click:outside="datePickerModal = false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateFormatted"
                      :label="$t('extTrainings.column.date')"
                      prepend-inner-icon="event"
                      @click:prepend-inner="datePickerModal = true"
                      v-on="on"
                      v-bind="attrs"
                      readonly
                      hide-details="auto"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="et.dateTimeOfOccurrence"
                    first-day-of-week="1"
                    @input="datePickerModal = false"
                    no-title
                    scrollable
                    :rules="[rules.required]"
                  ></v-date-picker>
                </v-dialog>
              </v-col>

              <v-col cols="6">
                <v-dialog
                  v-model="timePickerModal"
                  persistent
                  width="290px"
                  @click:outside="timePickerModal = false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="timeFormatted"
                      :label="$t('extTrainings.column.time')"
                      prepend-inner-icon="mdi-clock-outline"
                      @click:prepend-inner="timePickerModal = true"
                      v-on="on"
                      v-bind="attrs"
                      readonly
                      hide-details="auto"
                    ></v-text-field>
                  </template>

                  <v-time-picker
                    ref="timePicker"
                    v-model="time"
                    format="24hr"
                    @click:minute="timePickerModal = false"
                  />
                </v-dialog>
              </v-col>

              <v-col cols="6">
                <v-text-field
                  v-model="et.dueInMonths"
                  type="number"
                  :label="$t('extTrainings.dueInMonths')"
                  hide-details="auto"
                  :disabled="noOverDue"
                />
              </v-col>

              <v-col cols="6" class="d-flex align-center">
                <v-checkbox
                  v-model="noOverDue"
                  :label="$t('extTrainings.noOverDue')"
                  hide-details="auto"
                  class="mt-0 pt-0"
                />
              </v-col>

              <v-col cols="6" class="mt-2">
                <v-btn icon @click="isExpanded = !isExpanded" class="mr-2">
                  <v-icon>{{
                    isExpanded ? "expand_less" : "expand_more"
                  }}</v-icon>
                </v-btn>

                <span @click="isExpanded = !isExpanded" class="v-label">{{
                  $t("mentorship.actions.edit.content")
                }}</span>
              </v-col>
              <v-col cols="6" v-if="employee">
                <v-btn
                  v-if="!localFile"
                  class="pl-2"
                  v-model="localFile"
                  color="primary"
                  text
                  small
                  @click="uploadFile"
                >
                  <v-icon left>mdi-paperclip</v-icon>
                  {{ $t("extTrainings.certificate.add") }}
                </v-btn>
                <div v-else>
                  <a :href="localFile.url" target="_blank">
                    <v-icon class="mr-2" color="primary">mdi-paperclip</v-icon>
                    {{ localFile.name }}
                  </a>
                  <v-btn class="ml-1" small icon @click="localFile = null">
                    <v-icon>close</v-icon>
                  </v-btn>
                </div>
              </v-col>

              <input
                type="file"
                ref="certificateInput"
                style="display: none"
                accept=".pdf, .jpg, .jpeg, .png, .gif, .bmp, .docx"
                @change="attachLocalFile"
              />
            </v-row>
            <v-expand-transition>
              <v-card v-if="isExpanded" class="pa-3 mt-1">
                <v-row class="ma-0" justify="space-between">
                  <v-item-group class="v-btn-toggle">
                    <v-btn
                      @click="toggleItalic"
                      :class="{
                        'v-btn--active': editor && editor.isActive('italic'),
                      }"
                    >
                      <v-icon>format_italic</v-icon>
                    </v-btn>

                    <v-btn
                      @click="toggleBold"
                      :class="{
                        'v-btn--active': editor && editor.isActive('bold'),
                      }"
                    >
                      <v-icon>format_bold</v-icon>
                    </v-btn>

                    <v-btn
                      @click="toggleUnderline"
                      :class="{
                        'v-btn--active': editor && editor.isActive('underline'),
                      }"
                    >
                      <v-icon>format_underline</v-icon>
                    </v-btn>
                  </v-item-group>

                  <v-item-group class="v-btn-toggle">
                    <color-btn
                      :isActive="
                        editor
                          ? editor.isActive('textStyle', { color: '#000000' })
                          : false
                      "
                      color="#000000"
                      @click="setColor('#000000')"
                    />
                    <color-btn
                      :isActive="
                        editor
                          ? editor.isActive('textStyle', { color: '#F44336' })
                          : false
                      "
                      color="#F44336"
                      @click="setColor('#F44336')"
                    />
                    <color-btn
                      :isActive="
                        editor
                          ? editor.isActive('textStyle', { color: '#4CAF50' })
                          : false
                      "
                      color="#4CAF50"
                      @click="setColor('#4CAF50')"
                    />
                  </v-item-group>

                  <v-item-group class="v-btn-toggle">
                    <v-btn
                      @click="toggleBulletList"
                      :class="{
                        'v-btn--active':
                          editor && editor.isActive('bulletList'),
                      }"
                    >
                      <v-icon>format_list_bulleted</v-icon>
                    </v-btn>
                    <v-btn
                      @click="toggleOrderedList"
                      :class="{
                        'v-btn--active':
                          editor && editor.isActive('orderedList'),
                      }"
                    >
                      <v-icon>format_list_numbered</v-icon>
                    </v-btn>
                  </v-item-group>

                  <v-item-group class="v-btn-toggle">
                    <v-btn @click="toggleLink">
                      <v-icon>link</v-icon>
                    </v-btn>
                  </v-item-group>
                </v-row>
                <editor-content class="mt-4" :editor="editor" />
              </v-card>
            </v-expand-transition>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="done">
          {{ $t("dialog.cancel") }}
        </v-btn>
        <v-btn color="primary" text @click="save">
          {{ $t("dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import ColorBtn from "../mentorship/control/ColorBtn.vue";

import TagsQuery from "@/graphql/Tags.gql";

import UpdateExternalTrainingQuery from "@/graphql/mutation/extTraining/UpsertExtTraining.gql";
import AddExtTrainingWithEmployeeQuery from "@/graphql/mutation/extTraining/AddExtTrainingWithEmployee.gql";

export default {
  props: ["extTraining", "categories", "employee"],
  components: {
    EditorContent,
    ColorBtn,
  },

  data() {
    return {
      et: null,
      time: "13:00",
      datePickerModal: false,
      timePickerModal: false,
      rules: {
        number: (value) => {
          return /^\d+$/.test(value) || this.$i18n.t("validation.numeric");
        },
        required: (value) => !!value || this.$i18n.t("validation.required"),
      },
      noOverDue: false,
      isExpanded: false,
      editor: null,
      localFile: null,
    };
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  computed: {
    sortedCategories() {
      return this.categories
        .slice()
        .sort((a, b) => a.category.localeCompare(b.category));
    },
    dateFormatted() {
      return this.$options.filters.date(this.et.dateTimeOfOccurrence);
    },
    timeFormatted() {
      return this.$options.filters.time(this.et.dateTimeOfOccurrence);
    },
  },
  watch: {
    extTraining() {
      this.et = this.extTraining;

      if (this.extTraining) {
        const dt = this.extTraining.dateTimeOfOccurrence;
        this.et.dateTimeOfOccurrenceFormatted = this.$options.filters.dateTimeWithoutSeconds(
          dt
        );
        this.time = `${new Date(dt).getHours()}:${new Date(dt).getMinutes()}`;

        this.editor = new Editor({
          content: this.et.description,
          extensions: [StarterKit, Underline, TextStyle, Color, Link],
        });
      }
    },
    "et.dateTimeOfOccurrence"(newDateTime) {
      if (newDateTime) {
        if (typeof newDateTime === "string" && !newDateTime.includes("T")) {
          const timeArray = this.time.split(":");
          const dateWithTime = new Date(newDateTime).setHours(
            timeArray[0],
            timeArray[1],
            0
          );

          this.et.dateTimeOfOccurrence = new Date(dateWithTime).toISOString();
        }

        this.et.dateTimeOfOccurrenceFormatted = this.$options.filters.dateTimeWithoutSeconds(
          this.et.dateTimeOfOccurrence
        );
      }
    },
    time(newTime, oldTime) {
      if (newTime != oldTime && newTime.length >= 5 && newTime.includes(":")) {
        const timeArray = newTime.split(":");
        const dt = new Date(this.et.dateTimeOfOccurrence).setHours(
          timeArray[0],
          timeArray[1],
          0
        );
        this.et.dateTimeOfOccurrence = new Date(dt).toISOString();
      }
    },
    "et.dueInMonths": {
      handler(dueInMonthsNumber) {
        if (dueInMonthsNumber === 0) {
          this.noOverDue = true;
        }
      },
    },
    timePickerModal(val) {
      if (val && this.$refs.timePicker) {
        this.$nextTick(() => {
          this.$refs.timePicker.selectingHour = true;
        });
      }
    },
  },
  methods: {
    toggleLink() {
      const selectedText = this.editor.state.selection.$from.parent.textContent;
      if (!selectedText) {
        return;
      }

      this.editor
        .chain()
        .focus()
        .toggleLink({ href: selectedText, target: "_blank" })
        .run();
    },
    toggleUnderline() {
      this.editor
        .chain()
        .focus()
        .toggleUnderline()
        .run();
    },
    toggleBold() {
      this.editor
        .chain()
        .focus()
        .toggleBold()
        .run();
    },
    toggleItalic() {
      this.editor
        .chain()
        .focus()
        .toggleItalic()
        .run();
    },
    toggleBulletList() {
      this.editor
        .chain()
        .focus()
        .toggleBulletList()
        .run();
    },
    toggleOrderedList() {
      this.editor
        .chain()
        .focus()
        .toggleOrderedList()
        .run();
    },
    setColor(color) {
      this.editor
        .chain()
        .focus()
        .setColor(color)
        .run();
    },
    changeCategory(newCategoryId) {
      const defaultDueInMonths = this.categories.find(
        (e) => e.id == newCategoryId
      ).defaultDueInMonths;
      this.et.dueInMonths = defaultDueInMonths;
    },
    uploadFile() {
      this.$refs.certificateInput.click();
    },
    attachLocalFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.localFile = {
        file,
        name: file.name,
        url: URL.createObjectURL(file),
        isLocal: true,
      };

      event.target.value = "";
    },
    save() {
      if (!this.$refs.extTrainingEditForm.validate()) {
        return;
      }

      if (!this.et.dueInMonths || this.noOverDue) {
        this.et.dueInMonths = 0;
      }

      let descriptionHTML = this.editor.getHTML();
      if (!descriptionHTML || /^<p>\s*<\/p>$/.test(descriptionHTML)) {
        descriptionHTML = null;
      }

      const extTrainingInput = {
        id: this.et.id,
        name: this.et.name,
        categoryId: this.et.categoryId,
        dateTimeOfOccurrence: new Date(
          this.et.dateTimeOfOccurrence
        ).toISOString(),
        educator: this.et.educator,
        location: this.et.location,
        durationMinutes: this.et.durationMinutes,
        dueInMonths: this.et.dueInMonths,
        description: descriptionHTML,
      };

      const mutation = this.employee
        ? AddExtTrainingWithEmployeeQuery
        : UpdateExternalTrainingQuery;
      const variables = this.employee
        ? {
            in: extTrainingInput,
            employeeID: this.employee.id,
            file: this.localFile ? this.localFile.file : null,
          }
        : { in: extTrainingInput };

      this.$apollo
        .mutate({
          mutation,
          variables,
        })
        .then((res) => {
          if (!this.et.id) {
            this.$emit("created", res.data.extTraining.id);
            this.resetData();
            this.localFile = null;
            return;
          }

          this.done();
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: `${this.$i18n.t("instructions.edit.saveFailed")}: ${err}`,
          });
          this.done();
        });
    },
    done() {
      this.$emit("done");
      this.resetData();
    },
    resetData() {
      this.noOverDue = false;
    },
  },
  apollo: {
    tags: {
      query: TagsQuery,
      fetchPolicy: "cache-and-network",
    },
  },
};
</script>
<style>
.ProseMirror {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
}
.v-label {
  font-size: 14px;
}
</style>
